import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MeService } from '../services/me.service';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';

/**
 * HTTP Interceptor to automatically handle:
 * - Authentication headers
 * - Request logging
 * - Centralized handling of 401/403 errors
 * - Response transformation
 */
@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  constructor(private meService: MeService, private router: Router) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Only apply transformations to API requests (v1, v2, vps)
    if (/\/api\/(v\d+\/|vps\/)?/.test(req.url)) {
      req = this.addAuthHeaders(req);
      req = this.addCommonHeaders(req);
    }

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        return this.handleError(error, req);
      })
    );
  }

  /**
   * Adds authentication headers if the user is logged in
   */
  private addAuthHeaders(req: HttpRequest<any>): HttpRequest<any> {
    if (this.meService.token) {
      return req.clone({
        setHeaders: {
          Authorization: `Bearer ${this.meService.token}`,
        },
      });
    }
    return req;
  }

  /**
   * Adds common headers to all requests
   */
  private addCommonHeaders(req: HttpRequest<any>): HttpRequest<any> {
    const headers: { [key: string]: string } = {};

    // Inject persistent Device ID
    headers['X-Device-ID'] = this.getOrCreateDeviceId();

    // Add Content-Type only if not FormData
    if (!(req.body instanceof FormData) && !req.headers.has('Content-Type')) {
      headers['Content-Type'] = 'application/json';
    }

    // Add user_logged_in as a query parameter automatically
    if (this.meService.user?.id || this.meService.token) {
      const userParam =
        this.meService.user?.id?.toString() || this.meService.token;

      // Only add if not already present
      if (!req.params.has('user_logged_in')) {
        const newParams = req.params.set('user_logged_in', userParam);
        req = req.clone({ params: newParams });
      }
    }

    // Add headers if there are any to add
    if (Object.keys(headers).length > 0) {
      return req.clone({ setHeaders: headers });
    }

    return req;
  }

  /**
   * Fetches or generates the unique device ID in local storage natively.
   */
  private getOrCreateDeviceId(): string {
    let deviceId = localStorage.getItem('app_device_id');
    if (!deviceId) {
      if (typeof window !== 'undefined' && window.crypto && (window.crypto as any).randomUUID) {
        deviceId = (window.crypto as any).randomUUID();
      } else {
        deviceId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
          const random = (window.crypto && window.crypto.getRandomValues)
            ? window.crypto.getRandomValues(new Uint8Array(1))[0] & 15
            : (Math.random() * 16) | 0;
          return (char === 'x' ? random : (random & 0x3) | 0x8).toString(16);
        });
      }
      localStorage.setItem('app_device_id', deviceId);
    }
    return deviceId;
  }

  /**
   * Centralized HTTP error handling
   */
  private handleError(
    error: HttpErrorResponse,
    req: HttpRequest<any>
  ): Observable<never> {
    let errorMessage = 'An unexpected error has occurred';

    // Error log (development only)
    if (!environment.production) {
      console.error(`❌ ${req.method} ${req.url}`, error);
    }

    // Specific handling by status code
    switch (error.status) {
      case 401:
        this.handle401Error();
        errorMessage = 'Your session has expired. Please log in again.';
        break;

      case 403:
        errorMessage = 'You do not have permission to perform this action.';
        break;

      case 404:
        errorMessage = 'The requested resource was not found.';
        break;

      case 422:
        // Validation errors
        if (error.error && error.error.errors) {
          errorMessage = this.formatValidationErrors(error.error.errors);
        } else {
          errorMessage = 'The submitted data is not valid.';
        }
        break;

      case 429:
        errorMessage =
          'Too many requests. Please wait a moment before trying again.';
        break;

      case 500:
        errorMessage = 'Internal server error. Our team has been notified.';
        break;

      case 503:
        errorMessage =
          'The service is temporarily unavailable. Please try again later.';
        break;

      case 0:
        // Network error
        errorMessage =
          'Could not connect to the server. Please check your internet connection.';
        break;

      default:
        if (error.error && error.error.message) {
          errorMessage = error.error.message;
        }
    }

    // Create structured error object
    const structuredError = {
      message: errorMessage,
      status: error.status,
      statusText: error.statusText,
      url: req.url,
      method: req.method,
      timestamp: new Date().toISOString(),
      error: error.error,
      headers: error.headers,
    };

    return throwError(() => structuredError);
  }

  /**
   * Handles 401 (Unauthorized) errors
   */
  private handle401Error(): void {
    // Clear session data
    this.meService.forget();

    // Clear localStorage
    localStorage.removeItem('ix.token');
    localStorage.removeItem('loginItem');
    localStorage.removeItem('hasUserFcmToken');

    // Redirect to login
    this.router.navigate(['/admin/login']);
  }

  /**
   * Formats validation errors into a readable message
   */
  private formatValidationErrors(errors: any): string {
    if (Array.isArray(errors)) {
      return errors.map((error) => error.message || error).join(', ');
    }

    if (typeof errors === 'object') {
      const messages: string[] = [];
      Object.keys(errors).forEach((field) => {
        const fieldErrors = Array.isArray(errors[field])
          ? errors[field]
          : [errors[field]];
        fieldErrors.forEach((errorMsg) => {
          messages.push(`${field}: ${errorMsg}`);
        });
      });
      return messages.join(', ');
    }

    return errors.toString();
  }
}

/**
 * Provider to register the interceptor
 * Use in app.module.ts
 */
export const ApiInterceptorProvider = {
  provide: HTTP_INTERCEPTORS,
  useClass: ApiInterceptor,
  multi: true,
};
